// import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import useAccess from "../../hooks/useAccess";
import Select from "react-select";
import axios from "axios";
import "../../styles/LeftPanelPersonal.css";
import LeftSlideOver from "../../components/LeftSlideOver";
import IconActionBar from "../../components/IconActionBar";
import PipelineBar from "../../components/PipelineBar";
import TagEditorPage from "../../pages/Admin/TagEditorPage";
import SimpleWorkHistoryPage from "../../pages/Admin/SimpleWorkHistoryPage";
import AssignTrainingPage from "../../pages/Admin/AssignTrainingPage";
import AssignTasksPage from "../../pages/Admin/AssignTasksPage";
import StatusChangeModal from "../../components/StatusChangeModal";
import PositionChangeModal from "../../components/PositionChangeModal";
import LoadingOverlay from "../../components/LoadingOverlay";
import UserActivities, {
  AlertActivityPanel,
  EmailActivityPlaceholder,
} from "../../components/UserActivities";
import { formatPhoneNumber, phoneHref } from "../../utils/phone";
import PermissionNotice from "../../components/PermissionNotice";
import { notifyPermissionDenied } from "../../components/PermissionFeedback";

const ADMIN_UPDATE_REASON = "Admin Update";

const LeftPanelPersonal = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState({
    work_type: "",
    emp_category: "",
    emp_sub_category: "",
    position_full: "",
    status: "",
    emp_recruiter: "",
    driver_manager: "",
    lc_manager: "",
  });
  const [profilePhones, setProfilePhones] = useState(() => ({
    mobile: user?.mobile || "",
    home_phone: user?.home_phone || "",
  }));

  // Pipeline states (current & pending)
  const [pipelineCurrent, setPipelineCurrent] = useState(null); // current from server
  const [pipelinePending, setPipelinePending] = useState(null); // user selection not yet saved

  const [countries, setCountries] = useState([]);
  const [workType, setWorkType] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [positions, setPositions] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [tagLookup, setTagLookup] = useState({});
  const [profileTagIds, setProfileTagIds] = useState([]);
  const [profileTagValues, setProfileTagValues] = useState({});
  const [orgUnits, setOrgUnits] = useState([]);
  const [positionTypes, setPositionTypes] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [driverManagers, setDriverManagers] = useState([]);
  const [lcManagers, setLcManagers] = useState([]);
  const [slideOpen, setSlideOpen] = useState(false);
  const [slideTab, setSlideTab] = useState("tags");
  const [modalOpen, setModalOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);
  const [targetStatusLabel, setTargetStatusLabel] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isPositionSaving, setIsPositionSaving] = useState(false);
  const [baselinePositionFull, setBaselinePositionFull] = useState("");
  const [activitiesRefreshKey, setActivitiesRefreshKey] = useState(0);

  // 🔁 Position Change (manual change from LeftPanelPersonal)
  const [positionChange, setPositionChange] = useState({
    show: false,
    prev_start_date: "",
    prev_end_date: "",
    new_start_date: "",
    reason: "",
    note: "",
    create_new_profile: false,
    new_employee_id: "",
  });

  // 🧪 State: Access & Auth
  const { hasPermission, currentUser, permissionsLoaded } = useAccess();

  const PERMISSIONS = {
    users: {
      edit: hasPermission("AdminDashboard", "User", "edit"),
    },
  };

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const refreshAssignedTags = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(
        `${BASE_URL}/api/userTags/users/${user.id}/tags`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      setProfileTagIds(response.data.tags || []);
      setProfileTagValues(response.data.tagsWithValue || {});
    } catch (err) {
      console.error("Failed to fetch assigned tags:", err);
      setProfileTagIds([]);
      setProfileTagValues({});
    }
  }, [BASE_URL, user?.id]);

  useEffect(() => {
    refreshAssignedTags();
  }, [refreshAssignedTags]);

  const currentUserRoleIds = useMemo(() => {
    try {
      const parsed =
        typeof currentUser?.emp_roles === "string"
          ? JSON.parse(currentUser.emp_roles)
          : currentUser?.emp_roles;
      return Array.isArray(parsed) ? parsed.map(Number) : [];
    } catch {
      return [];
    }
  }, [currentUser]);

  const isSuperAdmin = currentUserRoleIds.includes(17);
  const positionChanged =
    String(form.position_full || "") !== String(baselinePositionFull || "");
  const isAdminUpdate = positionChange.reason === ADMIN_UPDATE_REASON;

  const openTagsSlide = () => {
    setSlideTab("tags");
    setSlideOpen(true);
  };

  const openWorkHistorySlide = () => {
    setSlideTab("workHistory");
    setSlideOpen(true);
  };

  const openTrainingSlide = () => {
    setSlideTab("trainings");
    setSlideOpen(true);
  };

  const openTaskSlide = () => {
    setSlideTab("task");
    setSlideOpen(true);
  };

  const openAlertSlide = () => {
    setSlideTab("alert");
    setSlideOpen(true);
  };

  const openEmailSlide = () => {
    setSlideTab("email");
    setSlideOpen(true);
  };

  useEffect(() => {
    if (!user?.id) return;
    setProfilePhones({
      mobile: user.mobile || "",
      home_phone: user.home_phone || "",
    });

    const fetchUserData = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/users/${user.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setForm({
          work_type: res.data.work_type || "",
          emp_category: res.data.emp_category || "",
          emp_sub_category: res.data.emp_sub_category || "",
          position_full: res.data.position_full || "",
          status: res.data.status || "",
          emp_recruiter: res.data.emp_recruiter || "",
          driver_manager: res.data.driver_manager || "",
          lc_manager: res.data.lc_manager || "",
          tags: res.data.tags || "",
          userid_no: res.data.userid_no || "",
        });
        setProfilePhones({
          mobile: res.data.mobile || "",
          home_phone: res.data.home_phone || "",
        });
        setBaselinePositionFull(res.data.position_full || "");
      } catch (err) {
        console.error("Failed to fetch user data:", err);
      }
    };

    fetchUserData();
  }, [user?.id, user?.mobile, user?.home_phone, BASE_URL]);

  useEffect(() => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    const fetchDropdowns = async () => {
      try {
        const [c1, w1, c2, sc, s1, orgRes, ptRes, r1, dm, lc, tagRes] =
          await Promise.all([
            axios.get(`${BASE_URL}/api/dropdowns/countries`, { headers }),
            axios.get(`${BASE_URL}/api/dropdowns/worktype`, { headers }),
            axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
            axios.get(`${BASE_URL}/api/dropdowns/sub-categories`, { headers }),
            axios.get(`${BASE_URL}/api/dropdowns/user-status`, { headers }),
            axios.get(`${BASE_URL}/api/dropdowns/org-units`, { headers }),
            axios.get(`${BASE_URL}/api/dropdowns/position-types`, { headers }),
            axios.get(`${BASE_URL}/api/dropdowns/recruiters`, { headers }),
            axios.get(`${BASE_URL}/api/dropdowns/driver-managers`, { headers }),
            axios.get(`${BASE_URL}/api/dropdowns/lc-managers`, { headers }),
            axios.get(`${BASE_URL}/api/dropdowns/tags`, { headers }),
          ]);
        setCountries(c1.data);
        setWorkType(w1.data);
        setCategories(c2.data);
        setSubCategories(sc.data);
        setStatusList(s1.data);
        setOrgUnits(orgRes.data);
        setPositionTypes(ptRes.data);
        setRecruiters(r1.data);
        setDriverManagers(dm.data);
        setLcManagers(lc.data);
        setTagLookup(
          tagRes.data.reduce((acc, t) => {
            acc[t.t_id] = t.t_name;
            return acc;
          }, {})
        );
      } catch (err) {
        console.error("Dropdown fetch error:", err);
      }
    };

    fetchDropdowns();
  }, [BASE_URL]);

  useEffect(() => {
    if (!form.emp_category) return;

    const fetchPositions = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/positions/by-category/${form.emp_category}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setPositions(res.data);
      } catch (err) {
        console.error("Failed to fetch positions:", err);
      }
    };

    fetchPositions();
  }, [BASE_URL, form.emp_category]);

  // Load current pipeline for this user (to know the active step)
  useEffect(() => {
    if (!user?.id) return;
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    (async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/pipeline/users/${user.id}`,
          {
            headers,
          }
        );
        const current = res.data?.current?.pipeline_status ?? null;
        setPipelineCurrent(current);
        setPipelinePending(null); // clear any previous pending when reloading user
      } catch (err) {
        console.error("Failed to fetch pipeline current:", err);
      }
    })();
  }, [user?.id, BASE_URL]);

  // 🔁 Load current active position history (for position change UI)
  useEffect(() => {
    if (!user?.id) return;

    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    axios
      .get(`${BASE_URL}/api/status-history/position/${user.id}`, { headers })
      .then((res) => {
        const active = res.data.find((p) => !p.ph_end_date);
        if (active) {
          setPositionChange((s) => ({
            ...s,
            prev_start_date: active.ph_start_date
              ? active.ph_start_date.slice(0, 10)
              : "",
          }));
        } else {
          setPositionChange((s) => ({
            ...s,
            prev_start_date:
              user?.user_hiring_date?.slice?.(0, 10) ||
              user?.user_created_date?.slice?.(0, 10) ||
              "",
          }));
        }
      })
      .catch((err) => {
        console.error("Failed to load active position history:", err);
      });
  }, [user?.id, user?.user_hiring_date, user?.user_created_date, BASE_URL]);

  const positionOptions = positions.map((pos) => {
    const orgName =
      orgUnits.find((o) => o.ou_id === parseInt(pos.p_org_unit))?.ou_name ||
      "Unknown Unit";
    const typeName =
      positionTypes.find((pt) => pt.pt_id === parseInt(pos.p_position_type))
        ?.pt_name || "Unknown Type";
    const city = pos.p_contact_info?.city || "";
    const openings = pos.p_posting?.p_num_openings ?? "N/A";

    return {
      value: pos.p_id, // ⬅️ use p_id
      title: pos.p_position_title,
      orgName,
      typeName,
      displayLabel: `${pos.p_position_title} - ${orgName} - ${typeName} - ${city}`,
      subtext: `Pos No: ${pos.p_position_number} - No. Openings: ${openings}`,
    };
  });

  const resetPositionChange = (overrides = {}) =>
    setPositionChange((previous) => ({
      show: false,
      prev_start_date: previous.prev_start_date || "",
      prev_end_date: "",
      new_start_date: "",
      reason: "",
      note: "",
      create_new_profile: false,
      new_employee_id: "",
      ...overrides,
    }));

  const cancelPositionChange = () => {
    setForm((previous) => ({
      ...previous,
      position_full: baselinePositionFull || "",
    }));
    resetPositionChange();
  };

  const savePositionChange = async () => {
    if (isPositionSaving) return;
    if (!positionChange.reason) {
      alert("Please select a reason for the position change.");
      return;
    }
    if (
      !isAdminUpdate &&
      (!positionChange.prev_end_date || !positionChange.new_start_date)
    ) {
      alert("Please enter both required position dates.");
      return;
    }
    setIsPositionSaving(true);
    try {
      await axios.post(
        `${BASE_URL}/api/users/${user.id}/change-position`,
        {
          new_position_id: form.position_full,
          new_position_start_date: isAdminUpdate
            ? null
            : positionChange.new_start_date,
          prev_position_end_date: isAdminUpdate
            ? null
            : positionChange.prev_end_date,
          reason_for_change: positionChange.reason,
          position_note: positionChange.note,
          emp_category: form.emp_category || null,
          create_new_profile: false,
          new_employee_id: null,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      setBaselinePositionFull(form.position_full || "");
      resetPositionChange();
      if (typeof onSaved === "function") await onSaved(user.id);
      alert("Position changed successfully.");
    } catch (error) {
      console.error("Position change failed:", error);
      alert(error.response?.data?.message || "Failed to change position.");
    } finally {
      setIsPositionSaving(false);
    }
  };

  const handleSave = async () => {
    if (!PERMISSIONS.users.edit) {
      notifyPermissionDenied("You don't have permission to edit user profiles.");
      return;
    }
    if (isSaving) return;
    setIsSaving(true);
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    const picked = positions.find(
      (p) => String(p.p_id) === String(form.position_full)
    );
    const derivedOrgUnit = picked?.p_org_unit || null;
    const derivedPositionTitle = picked?.p_position_title || null;

    try {
      // 1) Save minimal fields
      await axios.put(
        `${BASE_URL}/api/users/${user.id}/minimal`,
        {
          work_type: form.work_type,
          emp_category: form.emp_category,
          emp_sub_category: form.emp_sub_category,
          position_full: form.position_full, // p_id
          position: derivedPositionTitle, // ⬅️ new
          status: form.status,
          emp_recruiter: form.emp_recruiter,
          driver_manager: form.driver_manager,
          lc_manager: form.lc_manager,
          org_unit: derivedOrgUnit, // derived
          skip_position_update: false,
          position_change_mode: null,
        },
        { headers }
      );

      // 2) Save pipeline if changed and user is Candidate
      const shouldSavePipeline =
        Number(form.status) === 3 &&
        pipelinePending &&
        Number(pipelinePending) !== Number(pipelineCurrent);

      if (shouldSavePipeline) {
        await axios.post(
          `${BASE_URL}/api/pipeline/users/${user.id}`,
          { newStatusId: Number(pipelinePending) },
          { headers }
        );
        setPipelineCurrent(Number(pipelinePending));
        setPipelinePending(null);
      }

      // 🔔 tell parent to refresh
      // 🔔 refresh parent + re-fetch this slide data
      if (typeof onSaved === "function") {
        await onSaved(user.id);
      }

      // 🔄 reload this slide's data without closing
      const refreshed = await axios.get(`${BASE_URL}/api/users/${user.id}`, {
        headers,
      });

      setForm({
        work_type: refreshed.data.work_type || "",
        emp_category: refreshed.data.emp_category || "",
        emp_sub_category: refreshed.data.emp_sub_category || "",
        position_full: refreshed.data.position_full || "",
        status: refreshed.data.status || "",
        emp_recruiter: refreshed.data.emp_recruiter || "",
        driver_manager: refreshed.data.driver_manager || "",
        lc_manager: refreshed.data.lc_manager || "",
        tags: refreshed.data.tags || "",
        userid_no: refreshed.data.userid_no || "",
      });
      setBaselinePositionFull(refreshed.data.position_full || "");

      // 🔄 reset position change UI
      resetPositionChange({ prev_start_date: "" });

      alert("✔️ Saved successfully");
    } catch (err) {
      console.error("Save error:", err);
      alert("❌ Save failed");
    } finally {
      setIsSaving(false);
    }
  };

  const getCountryName = (id) => {
    const match = countries.find((c) => c.id === parseInt(id));
    return match?.country_name || id;
  };

  const isUsableProfileValue = (value) => {
    const normalized = String(value ?? "").trim().toLowerCase();
    return normalized && !["nan", "null", "undefined", "-"].includes(normalized);
  };

  const addressLines = [
    user.address1,
    [user.city, user.province].filter(isUsableProfileValue).join(", "),
    [user.postal_code, getCountryName(user.country)]
      .filter(isUsableProfileValue)
      .join(", "),
  ].filter(isUsableProfileValue);

  const visibleProfileTags = profileTagIds
    .map((tagId) => ({
      id: tagId,
      label: tagLookup[tagId] || `Tag ${tagId}`,
      value: profileTagValues[tagId],
    }))
    .filter(({ label }) => isUsableProfileValue(label));

  const mobilePhone = formatPhoneNumber(profilePhones.mobile);
  const homePhone = formatPhoneNumber(profilePhones.home_phone);

  return (
    <div className="left-slide-panel">
      {isSaving && <LoadingOverlay text="Saving changes..." />}
      <div className="panel-header">
        <h3>
          {user.first_name} {user.last_name}
        </h3>
        <div className="d-flex gap-2">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!PERMISSIONS.users.edit || isSaving}
          >
            Save
          </button>

          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {permissionsLoaded && !PERMISSIONS.users.edit && (
        <PermissionNotice resource="user profiles" compact />
      )}

      <div className="panel-sections-row">
        <div className="card panel-card profile-card">
          <div className="card-title">Profile</div>
          <div className="card-content profile-table">
            <div className="profile-row profile-address-row">
              <span className="profile-icon" aria-hidden="true">
                <i className="fas fa-map-marker-alt" />
              </span>
              <div className="profile-text">
                <span className="profile-field-label">Address</span>
                {addressLines.length ? (
                  <div className="profile-address-lines">
                    {addressLines.map((line, index) => (
                      <span key={`${line}-${index}`}>{line}</span>
                    ))}
                  </div>
                ) : (
                  <span className="profile-empty-value">Not provided</span>
                )}
              </div>
            </div>
            {mobilePhone && (
              <div className="profile-row">
                <span className="profile-icon" aria-hidden="true">
                  <i className="fas fa-mobile-alt" />
                </span>
                <div className="profile-text">
                  <span className="profile-field-label">Mobile</span>
                  <a href={phoneHref(profilePhones.mobile)}>{mobilePhone}</a>
                </div>
              </div>
            )}
            {homePhone && (
              <div className="profile-row">
                <span className="profile-icon" aria-hidden="true">
                  <i className="fas fa-phone-alt" />
                </span>
                <div className="profile-text">
                  <span className="profile-field-label">Home Phone</span>
                  <a href={phoneHref(profilePhones.home_phone)}>{homePhone}</a>
                </div>
              </div>
            )}
            <div className="profile-row">
              <span className="profile-icon" aria-hidden="true">
                <i className="fas fa-envelope" />
              </span>
              <div className="profile-text">
                <span className="profile-field-label">Email</span>
                {isUsableProfileValue(user.email) ? (
                  <a className="profile-email" href={`mailto:${user.email}`}>
                    {user.email}
                  </a>
                ) : (
                  <span className="profile-empty-value">Not provided</span>
                )}
              </div>
            </div>
            <div className="profile-row profile-tags-row">
              <button
                type="button"
                className="profile-icon profile-tags-icon-button"
                onClick={openTagsSlide}
                title="Manage tags"
                aria-label="Manage tags"
              >
                <i className="fas fa-tags" aria-hidden="true" />
              </button>
              <div className="profile-text">
                <span className="profile-field-label">Tags</span>
                <div className="profile-tag-list">
                  {visibleProfileTags.length ? (
                    visibleProfileTags.map(({ id, label, value }) => (
                      <span key={id} className="tag-label">
                        {label}
                        {isUsableProfileValue(value) ? ` - ${value}` : ""}
                      </span>
                    ))
                  ) : (
                    <span className="profile-empty-value">No tags assigned</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* ===== Actions (clean layout) ===== */}
        <div className="card panel-card actions-card">
          <div className="card-title">Actions</div>

          <div className="card-content actions-content">
            {/* Top icon bar */}
            <IconActionBar
              items={[
                {
                  key: "tags",
                  title: "Tags",
                  icon: <i className="fa fa-tags" />,
                  onClick: openTagsSlide,
                },
                {
                  key: "workHistory",
                  title: "Work History",
                  icon: <i className="fa fa-id-card" />,
                  onClick: openWorkHistorySlide,
                },
                {
                  key: "trainings",
                  title: "Online Training Assignment",
                  icon: <i className="fas fa-graduation-cap" />,
                  onClick: openTrainingSlide,
                },
                {
                  key: "tasks",
                  title: "Tasks Assignment",
                  icon: <i className="fas fa-tasks" />,
                  onClick: openTaskSlide,
                },
              ]}
              onSelect={(key) => {
                if (key === "tags") openTagsSlide();
                if (key === "workHistory") openWorkHistorySlide();
                if (key === "trainings") openTrainingSlide();
                if (key === "tasks") openTaskSlide();
              }}
            />

            {/* Normalized react-select styles/hooks */}
            {(() => {
              const RS_STYLES = {
                control: (base) => ({
                  ...base,
                  minHeight: 36,
                  height: 36,
                  borderColor: "#c9ccd1",
                  boxShadow: "none",
                  borderRadius: 8,
                }),
                valueContainer: (base) => ({ ...base, padding: "2px 10px" }),
                indicatorsContainer: (b) => ({ ...b, height: 36 }),
                dropdownIndicator: (b) => ({ ...b, padding: "6px 8px" }),
                clearIndicator: (b) => ({ ...b, padding: "6px 8px" }),
                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                menu: (b) => ({ ...b, zIndex: 9999 }),
              };

              const RS_COMMON = {
                classNamePrefix: "rs",
                styles: RS_STYLES,
                menuPosition: "fixed",
                menuPortalTarget:
                  typeof document !== "undefined" ? document.body : null,
              };

              return (
                <>
                  {/* This position is */}
                  <div className="field-row">
                    <label className="field-label">This position is:</label>
                    <Select
                      {...RS_COMMON}
                      name="work_type"
                      value={
                        workType.find(
                          (w) => w.wt_id === Number(form.work_type)
                        ) || null
                      }
                      onChange={(v) =>
                        setForm((p) => ({ ...p, work_type: v?.wt_id || "" }))
                      }
                      options={workType}
                      getOptionLabel={(e) => e.wt_name}
                      getOptionValue={(e) => e.wt_id}
                      isClearable
                      isDisabled={!PERMISSIONS.users.edit}
                    />
                  </div>

                  {/* Employment Category / Sub-Category */}
                  <div className="field-card">
                    <div className="row-two">
                      <div className="form-group">
                        <label>Employment Category:</label>
                        <div className="with-action-icon">
                          <Select
                            {...RS_COMMON}
                            name="emp_category"
                            value={
                              categories.find(
                                (c) => c.c_id === Number(form.emp_category)
                              ) || null
                            }
                            onChange={(v) =>
                              setForm((p) => ({
                                ...p,
                                emp_category: v?.c_id || "",
                                emp_sub_category: "",
                              }))
                            }
                            options={categories}
                            placeholder="Company Driver / Office Staff..."
                            getOptionLabel={(e) => e.c_name}
                            getOptionValue={(e) => e.c_id}
                            isDisabled={!PERMISSIONS.users.edit}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Sub-Category:</label>
                        <Select
                          {...RS_COMMON}
                          name="emp_sub_category"
                          value={
                            subCategories.find(
                              (s) => s.sc_id === Number(form.emp_sub_category)
                            ) || null
                          }
                          onChange={(v) =>
                            setForm((p) => ({
                              ...p,
                              emp_sub_category: v?.sc_id || "",
                            }))
                          }
                          options={subCategories}
                          placeholder="Select sub-category"
                          getOptionLabel={(e) => e.sc_name}
                          getOptionValue={(e) => e.sc_id}
                          isClearable
                          isDisabled={!PERMISSIONS.users.edit}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Primary / Secondary Position(s) */}
                  <div className="form-group">
                    <label>Primary Position:</label>
                    <Select
                      {...RS_COMMON}
                      name="position_full"
                      value={
                        positionOptions.find(
                          (o) => o.value === Number(form.position_full)
                        ) || null
                      }
                      onChange={(v) => {
                        const selectedValue = v?.value || "";
                        setForm((p) => ({
                          ...p,
                          position_full: selectedValue,
                        }));

                        // 🔁 trigger position change UI
                        setPositionChange((s) => {
                          const hasRealChange =
                            String(selectedValue) !==
                            String(baselinePositionFull || "");

                          return hasRealChange
                            ? {
                                ...s,
                                show: true,
                                prev_end_date: "",
                                new_start_date: "",
                                reason: "",
                                note: "",
                                create_new_profile: false,
                                new_employee_id: "",
                              }
                            : {
                                ...s,
                                show: false,
                                prev_end_date: "",
                                new_start_date: "",
                                reason: "",
                                note: "",
                                create_new_profile: false,
                                new_employee_id: "",
                              };
                        });
                      }}
                      options={positionOptions}
                      placeholder="Select primary position"
                      isSearchable
                      getOptionLabel={(e) => e.displayLabel}
                      formatOptionLabel={(data, { context }) =>
                        context === "menu" ? (
                          <div>
                            <div className="react-select-label">
                              {data.displayLabel}
                            </div>
                            <div className="react-select-subtext">
                              {data.subtext}
                            </div>
                          </div>
                        ) : (
                          `${data.title} - ${data.orgName} - ${data.typeName}`
                        )
                      }
                      isDisabled={!PERMISSIONS.users.edit}
                    />
                  </div>

                  {/* 🔁 Position Change Details */}
                  {positionChange.show && (
                    <div className="form-group position-change-box">
                      <div className="section-divider">
                        <strong>Previous Position</strong>
                      </div>

                      <label>Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={positionChange.prev_start_date || ""}
                        disabled
                      />

                      {!isAdminUpdate && (
                        <>
                          <label>End Date *</label>
                          <input
                            type="date"
                            className="form-control"
                            value={positionChange.prev_end_date || ""}
                            onChange={(e) =>
                              setPositionChange((p) => ({
                                ...p,
                                prev_end_date: e.target.value,
                              }))
                            }
                            disabled={!PERMISSIONS.users.edit}
                          />
                        </>
                      )}

                      <div className="section-divider mt-2">
                        <strong>New Position</strong>
                      </div>

                      {!isAdminUpdate && (
                        <>
                          <label>New Position Start Date *</label>
                          <input
                            type="date"
                            className="form-control"
                            value={positionChange.new_start_date || ""}
                            onChange={(e) =>
                              setPositionChange((p) => ({
                                ...p,
                                new_start_date: e.target.value,
                              }))
                            }
                            disabled={!PERMISSIONS.users.edit}
                          />
                        </>
                      )}

                      <label>Reason for Change *</label>
                      <select
                        className="form-control"
                        value={positionChange.reason}
                        onChange={(e) =>
                          setPositionChange((p) => ({
                            ...p,
                            reason: e.target.value,
                            prev_end_date:
                              e.target.value === ADMIN_UPDATE_REASON
                                ? ""
                                : p.prev_end_date,
                            new_start_date:
                              e.target.value === ADMIN_UPDATE_REASON
                                ? ""
                                : p.new_start_date,
                          }))
                        }
                        disabled={!PERMISSIONS.users.edit}
                      >
                        <option value="">Select Reason</option>
                        <option value="API transfer">API transfer</option>
                        <option value="Driver Rebuttal">Driver Rebuttal</option>
                        <option value="Transfer">Transfer</option>
                        {isSuperAdmin && (
                          <option value={ADMIN_UPDATE_REASON}>
                            {ADMIN_UPDATE_REASON}
                          </option>
                        )}
                      </select>

                      <label>Position Change Note</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={positionChange.note}
                        onChange={(e) =>
                          setPositionChange((p) => ({
                            ...p,
                            note: e.target.value,
                          }))
                        }
                        disabled={!PERMISSIONS.users.edit}
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Secondary Position(s):</label>
                    <Select
                      {...RS_COMMON}
                      name="secondary_positions"
                      isMulti
                      isClearable
                      value={(form.secondary_positions || [])
                        .map((id) =>
                          positionOptions.find((o) => o.value === Number(id))
                        )
                        .filter(Boolean)}
                      onChange={(arr) =>
                        setForm((p) => ({
                          ...p,
                          secondary_positions: (arr || []).map((o) => o.value),
                        }))
                      }
                      options={positionOptions}
                      placeholder="Add secondary positions (optional)"
                      getOptionLabel={(e) => e.displayLabel}
                      isDisabled={!PERMISSIONS.users.edit}
                    />
                  </div>

                  {/* Status */}
                  <div className="status-block">
                    <div className="status-line">
                      <span className="status-label">Current status</span>
                      <span className="status-chip">
                        {statusList.find((s) => s.s_id === Number(form.status))
                          ?.s_TranslatedText || "—"}
                      </span>
                    </div>

                    <div className="move-to">
                      <span className="move-to-label">Change status</span>
                      <div className="status-action-list">
                        {[
                          "Associate",
                          "Candidate",
                          "Future Candidate",
                          "Prospect",
                          "Inactive",
                        ].map((label) => {
                          const match = statusList.find(
                            (s) =>
                              (s.s_TranslatedText || "").trim() === label
                          );
                          const matchId = match?.s_id;

                          if (!matchId || matchId === Number(form.status))
                            return null;

                          return (
                            <button
                              key={label}
                              type="button"
                              className="btn-chip"
                              onClick={() => {
                                setTargetStatus(matchId);
                                setTargetStatusLabel(label);
                                setModalOpen(true);
                              }}
                              disabled={!PERMISSIONS.users.edit}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Managers */}
                  <div className="row-three">
                    <div className="form-group">
                      <label>Recruiter:</label>
                      <Select
                        {...RS_COMMON}
                        name="emp_recruiter"
                        value={
                          recruiters.find(
                            (r) => r.value === Number(form.emp_recruiter)
                          ) || null
                        }
                        onChange={(v) =>
                          setForm((p) => ({
                            ...p,
                            emp_recruiter: v?.value || "",
                          }))
                        }
                        options={recruiters}
                        placeholder="Select recruiter"
                        isSearchable
                        isClearable
                        isDisabled={!PERMISSIONS.users.edit}
                      />
                    </div>

                    <div className="form-group">
                      <label>Driver Manager:</label>
                      <Select
                        {...RS_COMMON}
                        name="driver_manager"
                        value={
                          driverManagers.find(
                            (r) => r.value === Number(form.driver_manager)
                          ) || null
                        }
                        onChange={(v) =>
                          setForm((p) => ({
                            ...p,
                            driver_manager: v?.value || "",
                          }))
                        }
                        options={driverManagers}
                        placeholder="Select driver manager"
                        isSearchable
                        isClearable
                        isDisabled={!PERMISSIONS.users.edit}
                      />
                    </div>

                    <div className="form-group">
                      <label>Learning Centre Manager:</label>
                      <Select
                        {...RS_COMMON}
                        name="lc_manager"
                        value={
                          lcManagers.find(
                            (r) => r.value === Number(form.lc_manager)
                          ) || null
                        }
                        onChange={(v) =>
                          setForm((p) => ({ ...p, lc_manager: v?.value || "" }))
                        }
                        options={lcManagers}
                        placeholder="Select LC manager"
                        isSearchable
                        isClearable
                        isDisabled={!PERMISSIONS.users.edit}
                      />
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ==== Pipeline (Candidates only) ==== */}
      <div className="panel-sections-row">
        {Number(form.status) === 3 && (
          <div className="card panel-card pipeline-panel-card">
            <PipelineBar
              userId={user.id}
              show={true}
              canEdit={PERMISSIONS.users.edit}
              selectedId={pipelinePending || pipelineCurrent}
              onSelect={(id) => setPipelinePending(id)}
            />
          </div>
        )}
      </div>

      <div className="panel-sections-row">
        <UserActivities
          userId={user.id}
          refreshKey={activitiesRefreshKey}
          onOpenAlert={openAlertSlide}
          onOpenEmail={openEmailSlide}
        />
      </div>

      {/* ===== Reusable Left Slide-Over ===== */}
      <LeftSlideOver
        isOpen={slideOpen}
        title={`${user.first_name} ${user.last_name}`}
        onClose={() => setSlideOpen(false)}
        items={[
          {
            key: "alert",
            title: "Alert Message and Requested Documents",
            icon: <i className="fa fa-exclamation-circle" />,
            render: () => (
              <AlertActivityPanel
                user={user}
                onSent={() => setActivitiesRefreshKey((value) => value + 1)}
              />
            ),
          },
          {
            key: "email",
            title: "Email",
            icon: <i className="fa fa-envelope" />,
            render: () => <EmailActivityPlaceholder />,
          },
          {
            key: "tags",
            title: "Tags",
            icon: <i className="fa fa-tags" />, // or your own SVG
            render: () => (
              <TagEditorPage
                userId={user.id}
                onTagsSaved={refreshAssignedTags}
              />
            ),
          },
          {
            key: "workHistory",
            title: "Work History",
            icon: <i className="fa fa-id-card" />,
            render: () => <SimpleWorkHistoryPage userId={user.id} />,
          },
          {
            key: "trainings",
            title: "Online Training Assignment",
            icon: <i className="fas fa-graduation-cap" />,
            render: () => <AssignTrainingPage userId={user.id} />,
          },
          {
            key: "task",
            title: "Task Assignment",
            icon: <i className="fas fa-tasks" />,
            render: () => <AssignTasksPage userId={user.id} />,
          },
          // add more items with icons as needed
        ]}
        activeKey={slideTab}
        onChange={setSlideTab}
        showHeader
      />

      <PositionChangeModal
        isOpen={positionChange.show && positionChanged}
        onCancel={cancelPositionChange}
        onSave={savePositionChange}
        data={positionChange}
        onChange={setPositionChange}
        currentPositionLabel={
          positionOptions.find(
            (option) => String(option.value) === String(baselinePositionFull),
          )?.displayLabel
        }
        newPositionLabel={
          positionOptions.find(
            (option) => String(option.value) === String(form.position_full),
          )?.displayLabel
        }
        isSuperAdmin={isSuperAdmin}
        isSaving={isPositionSaving}
      />

      <StatusChangeModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        userId={user.id}
        currentStatus={form.status}
        newStatus={targetStatus}
        newStatusLabel={targetStatusLabel} // 👈 add this
        onSuccess={() => {
          setForm((prev) => ({ ...prev, status: targetStatus }));
          alert("✔️ Status updated");
        }}
      />
    </div>
  );
};

export default LeftPanelPersonal;
